<template>
  <div class="integrated-comparison" data-testid="settlement-comparison-page">
    <header class="page-bar">
      <div>
        <h1>{{ $t('플랫폼 비교') }}</h1>
        <SettlementStatusBadge :badges="badges" />
      </div>
      <el-button size="small" icon="el-icon-back" @click="$router.push('/v/business/integrated-sales')">
        {{ $t('대시보드') }}
      </el-button>
    </header>

    <section class="filter-band">
      <el-date-picker
        v-model="dateRange"
        size="small"
        type="daterange"
        value-format="yyyy-MM-dd"
        :range-separator="$t('~')"
        :start-placeholder="$t('시작일')"
        :end-placeholder="$t('종료일')">
      </el-date-picker>
      <el-select v-model="platform" size="small" clearable :placeholder="$t('플랫폼')" class="platform-select">
        <el-option label="전체" value=""></el-option>
        <el-option v-for="p in deliveryPlatformFilters" :key="p" :label="platformDisplayName(p)" :value="p"></el-option>
      </el-select>
      <el-button type="primary" size="small" icon="el-icon-search" @click="loadComparison">{{ $t('검색') }}</el-button>
    </section>

    <el-alert v-if="errorMessage" type="error" :closable="false" show-icon class="state-alert">
      <span slot="title">{{ errorMessage }}</span>
    </el-alert>

    <div v-if="loading" class="state-panel" aria-busy="true">
      <i class="el-icon-loading"></i>
      <span>{{ $t('플랫폼 비교를 불러오는 중입니다') }}</span>
    </div>

    <el-table
      v-else
      :data="matrixRows"
      size="small"
      border
      stripe
      :empty-text="$t('데이타 없음')"
      class="comparison-table">
      <el-table-column :label="$t('항목')" prop="label" min-width="170" fixed></el-table-column>
      <el-table-column :label="$t('합계')" prop="total" min-width="130" align="right">
        <template slot-scope="scope">{{ formatMatrixValue(scope.row.total, scope.row.type) }}</template>
      </el-table-column>
      <el-table-column
        v-for="platformName in platformColumns"
        :key="platformName"
        :label="platformDisplayName(platformName)"
        min-width="130"
        align="right">
        <template slot-scope="scope">{{ formatMatrixValue(scope.row.values[platformName], scope.row.type) }}</template>
      </el-table-column>
    </el-table>

    <section v-if="platformColumns.length" class="detail-actions">
      <el-button
        v-for="platformName in platformColumns"
        :key="platformName"
        size="small"
        icon="el-icon-document"
        @click="goDetail(platformName)">
        {{ platformDisplayName(platformName) }}
      </el-button>
    </section>
  </div>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import { util } from '@/mixins/utilMixin'
import SettlementStatusBadge from '@/components/integratedSales/SettlementStatusBadge.vue'
import { deductionComponentValue, hasServerDeductionTotal } from '@/utils/integratedSalesDeductions'
import { DELIVERY_PLATFORM_FILTERS, platformDisplayName } from '@/utils/deliveryPlatform'

export default {
  mixins: [mainVueMixin],
  components: { SettlementStatusBadge },
  data() {
    return {
      dateRange: [
        this.$route.query.startDate || util.getDate('yyyy-MM-dd', util.dateAdd('d ', -30, new Date())),
        this.$route.query.endDate || util.getNow('yyyy-MM-dd'),
      ],
      platform: this.$route.query.platform || '',
      deliveryPlatformFilters: DELIVERY_PLATFORM_FILTERS,
      platforms: [],
      badges: {},
      loading: false,
      errorMessage: '',
    }
  },
  computed: {
    normalizedPlatforms() {
      return this.platforms.map((row) => {
        const orderAmount = Number(row.order_amount || 0)
        const serverSigned = hasServerDeductionTotal(row)
        const normalizedRow = {
          ...row,
          delivery_fee: deductionComponentValue(row, 'delivery_fee', row.platform, serverSigned),
          store_delivery_tip: deductionComponentValue(row, 'store_delivery_tip', row.platform, serverSigned),
          platform_discount: deductionComponentValue(row, 'platform_discount', row.platform, serverSigned),
        }
        const categoryTotal =
          Number(normalizedRow.delivery_fee || 0) +
          Number(normalizedRow.store_delivery_tip || 0) +
          Number(normalizedRow.platform_discount || 0) +
          Number(row.service_commission || 0) +
          Number(row.card_commission || 0) +
          Number(row.store_discount || 0) +
          Number(row.vat || 0) +
          Number(row.ad_cost || 0) +
          Number(row.instant_discount || 0)
        const estimatedFee = Number(row.estimated_fee || 0)
        const deductionAmount = (serverSigned ? Number(row.deduction_amount || 0) : categoryTotal) + estimatedFee
        return {
          ...normalizedRow,
          estimated_fee: estimatedFee,
          deduction_amount: deductionAmount,
          deduction_rate: orderAmount ? (deductionAmount / orderAmount) * 100 : 0,
        }
      })
    },
    platformColumns() {
      return this.normalizedPlatforms.map((row) => row.platform)
    },
    matrixRows() {
      const metrics = [
        { key: 'transaction_count', label: this.$t('주문건수'), type: 'count' },
        { key: 'order_amount', label: this.$t('총매출액'), type: 'currency' },
        { key: 'deduction_amount', label: this.$t('총차감액'), type: 'currency' },
        { key: 'deduction_rate', label: this.$t('차감률'), type: 'percent', total: () => this.totalDeductionRate() },
        { key: 'delivery_fee', label: this.$t('배달요금'), type: 'currency' },
        { key: 'store_delivery_tip', label: this.$t('가게배달팁(수취)'), type: 'currency' },
        { key: 'service_commission', label: this.$t('중개수수료'), type: 'currency' },
        { key: 'card_commission', label: this.$t('결제대행'), type: 'currency' },
        { key: 'platform_discount', label: this.$t('플랫폼 할인'), type: 'currency' },
        { key: 'store_discount', label: this.$t('가게부담 할인'), type: 'currency' },
        { key: 'vat', label: this.$t('부가세'), type: 'currency' },
        { key: 'ad_cost', label: this.$t('광고비'), type: 'currency' },
        { key: 'instant_discount', label: this.$t('즉시할인'), type: 'currency' },
        { key: 'estimated_fee', label: this.$t('미수집 38% 추정'), type: 'currency' },
        { key: 'expected_settlement', label: this.$t('정산 예상'), type: 'currency' },
      ]

      return metrics.map((metric) => {
        const values = this.normalizedPlatforms.reduce((acc, row) => {
          acc[row.platform] = Number(row[metric.key] || 0)
          return acc
        }, {})
        return {
          ...metric,
          values,
          total: metric.total
            ? metric.total()
            : Object.values(values).reduce((sum, value) => sum + Number(value || 0), 0),
        }
      })
    },
  },
  created() {
    this.loadComparison()
  },
  methods: {
    platformDisplayName,
    buildParams() {
      return {
        startDate: this.dateRange?.[0],
        endDate: this.dateRange?.[1],
        platform: this.platform || undefined,
      }
    },
    async loadComparison() {
      this.loading = true
      this.errorMessage = ''
      try {
        const outcome = await this.mainVue.$axios.search(
          '/api/statistics/integrated-sales/comparison',
          this.buildParams(),
          (result) => {
            this.platforms = result.platforms || []
            this.badges = result.badges || {}
          },
          false
        )
        // 요청 실패는 헬퍼가 값으로 알린다 — 아래 catch 한 곳이 안내한다(그 밖의 오류와 같은 문장)
        if (!outcome.ok) throw outcome.error
      } catch (error) {
        this.errorMessage = this.$t('플랫폼 비교를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.')
      } finally {
        this.loading = false
      }
    },
    goDetail(platform) {
      this.$router
        .push({ path: '/v/business/integrated-sales/detail', query: { ...this.buildParams(), platform } })
        .catch(() => {})
    },
    totalDeductionRate() {
      const orderAmount = this.normalizedPlatforms.reduce((sum, row) => sum + Number(row.order_amount || 0), 0)
      const deductionAmount = this.normalizedPlatforms.reduce((sum, row) => sum + Number(row.deduction_amount || 0), 0)
      return orderAmount ? (deductionAmount / orderAmount) * 100 : 0
    },
    formatMatrixValue(value, type) {
      if (type === 'percent') {
        return `${Number(value || 0).toFixed(1)}%`
      }
      if (type === 'count') {
        return `${Number(value || 0).toLocaleString()}${this.$t('건')}`
      }
      return `${Number(value || 0).toLocaleString()}원`
    },
  },
}
</script>

<style scoped>
.integrated-comparison {
  min-height: calc(100vh - 120px);
  padding: 16px;
  background: var(--color-bg-page);
}

.page-bar,
.filter-band {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

h1 {
  margin: 0 0 10px;
  color: var(--color-text-primary);
  font-size: 18px;
  font-weight: 700;
}

.filter-band {
  justify-content: flex-start;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  box-shadow: var(--shadow-card);
}

.platform-select {
  width: 150px;
}

.comparison-table {
  min-width: 0;
  border-radius: var(--radius-md);
  overflow: hidden;
}

.state-alert {
  margin-bottom: 12px;
}

.state-panel {
  display: flex;
  min-height: 140px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 12px 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  color: var(--color-text-regular);
}

.detail-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

@media (max-width: 768px) {
  .integrated-comparison {
    padding: 10px;
  }

  .filter-band .el-date-editor,
  .platform-select,
  .filter-band .el-button {
    width: 100%;
  }
}
</style>
